import React, { forwardRef } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface FormSelectOption {
  id: string | number;
  value: string;
  label: string;
}

export interface FormSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FormSelectOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  clearable?: boolean;
}

const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      value,
      onChange,
      options,
      placeholder = "Select an option",
      label,
      disabled = false,
      required = false,
      error,
      className,
      onKeyDown,
      onBlur,
      clearable = true,
      ...props
    },
    ref
  ) => {
    return (
      <div className={cn("w-full", className)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            disabled={disabled}
            className={cn(
              "w-full h-10 px-3 py-2 pr-8 text-sm bg-[#f0f0f0] text-[#444444] border border-[#c4c4c4] rounded-md appearance-none cursor-pointer",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              error && "border-red-500 focus:ring-red-500"
            )}
            {...props}
          >
            <option value="">
              {placeholder}
            </option>
            {(() => {
              // Add N/A option to options
              const hasNAOption = options.some(option => option.id === 0);
              const optionsWithNA = hasNAOption ? options : [{ id: 0, value: 'N/A', label: 'N/A' }, ...options];

              return optionsWithNA.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ));
            })()}
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {clearable && value && !disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange('');
                }}
                className="mr-1 p-1 hover:bg-gray-200 rounded-full transition-colors"
                tabIndex={-1}
              >
                <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
    );
  }
);

FormSelect.displayName = 'FormSelect';

export { FormSelect };
